/**
 * Debug-log client API — thin wrappers over the permission-gated
 * /api/debug-log/:kind/:id routes (mini-app debug log). Used by the
 * DebugLogViewer opened from the file context menu.
 */
import { request } from '@papan/api-client'

export type DebugLogKind = 'miniapp' | 'html'

/** One log line (mirrors the server core's DebugLogEntry — kept as a local,
 *  client-safe shape so the web bundle needn't import server code). */
export interface DebugLogEntry {
  ts: number
  sess: string
  ver?: string
  src: 'browser' | 'backend' | 'meta' | 'export'
  lvl?: 'log' | 'info' | 'debug' | 'warn' | 'error' | 'network'
  msg?: string
  stack?: string
  url?: string
  status?: number
  script?: string
  /** in-app file the entry came from (miniapp consolidated log), e.g. "a.html" */
  file?: string
  event?: string
  actor?: string
  ua?: string
  n?: number
}

/** Resolve a drive entry to its debug-log target. Callers should first gate
 *  with `supportsDebugLog(name, isFolder)`; a `.miniapp` folder logs as the
 *  folder, a loose file (HTML / .py / .sh) as the file — the `html` kind is
 *  the historical wire name for any loose file. */
export function debugLogTargetFor(file: { id: string; name: string; isFolder: boolean }): { kind: DebugLogKind; id: string } {
  return file.isFolder ? { kind: 'miniapp', id: file.id } : { kind: 'html', id: file.id }
}

export const debugLogApi = {
  /** Fetch the log entries (oldest → newest). Throws on 403 (no write access). */
  async get(kind: DebugLogKind, id: string): Promise<DebugLogEntry[]> {
    const res = await request<{ entries: DebugLogEntry[] }>(`/api/debug-log/${kind}/${encodeURIComponent(id)}`)
    return res.entries ?? []
  },
  /** Clear the log. `withinMs` scopes the clear to entries from the last
   *  <withinMs> ms (e.g. 3_600_000 = the last hour); omit to clear all. */
  async clear(kind: DebugLogKind, id: string, withinMs?: number): Promise<void> {
    const qs = withinMs !== undefined ? `?withinMs=${withinMs}` : ''
    await request(`/api/debug-log/${kind}/${encodeURIComponent(id)}${qs}`, { method: 'DELETE' })
  },
  /** Append entries to the log. Server-side sanitization/redaction applies;
   *  throws on 403 (no write access) — callers that report opportunistically
   *  (e.g. export errors) should swallow failures. */
  async append(kind: DebugLogKind, id: string, entries: Partial<DebugLogEntry>[]): Promise<void> {
    await request(`/api/debug-log/${kind}/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: JSON.stringify({ entries }),
    })
  },
}
